import { IBranch, IDatahubCase, ISimplifiedOrder, OrdersApi, UsersApi } from 'api'
import { useCases, useSimplifiedCaseOrders } from 'modules/cases/hooks'
import { UIEvent, useCallback, useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { useLocalization } from 'localization'
import { useQuery } from '@tanstack/react-query'
import QUERIES from 'modules/common/queries'
import { useShowModal } from 'modules/common/GlobalModals'
import { useNewOrderContext } from './context'
import { useHideModal } from 'components'
import { useAuth } from 'modules/auth/hooks'
import { mapPropertyTypeFromDto } from 'api/orders/helpers'

export function useContactPersons() {
	const { locale } = useLocalization()
	const { 
    data: contactPersonTypes = [],
    isLoading, 
    isError: isContactPersonError,
    refetch: onRefetchContactPerson 
        } = useQuery({
		queryKey: [QUERIES.contactPersonTypes],
		queryFn: UsersApi.getContactPersonTypes,
	})
	const contactPersonTypeOptions = useMemo(
		() =>
			contactPersonTypes.map(option => ({
				value: option.key,
				label: option.i18n.find(({ languageCode }) => languageCode === locale)?.text,
			})),
		[contactPersonTypes, locale]
	)

	return { contactPersonTypeOptions, isLoading, isContactPersonError, onRefetchContactPerson }
}

export function useCreateOrderCaseAutocomplete(departmentId?: IBranch['id']) {
	const { t } = useLocalization()
	const { user } = useAuth()
	const [query, setQuery] = useState('')
	const [selectedCaseId, setSelectedCaseId] = useState('')
	const isValidQuery = query.length >= 3
	const { data, isLoading, hasNextPage, fetchNextPage } = useCases(
		{
			query,
			departmentId: departmentId ? [departmentId] : undefined,
			includeNoOrders: true,
		},
		isValidQuery
	)
	const [isStillLoading] = useDebounce(isLoading, 500)

	const existingCaseOptions = useMemo(
		() =>
			data.map(caseObj => ({
				value: caseObj.reference,
				data: caseObj,
				label: `${caseObj.reference}${caseObj.address.trim() ? ` (${caseObj.address})` : ''}`,
			})),
		[data]
	)

	const newCaseOption = useMemo(() => {
		const foundDepartment = user?.branches.find(currentDepartment => currentDepartment.id === departmentId)
		const castedQuery = foundDepartment?.useUpperCase ? query.toUpperCase() : query

		return isValidQuery && !data.find(({ reference }) => reference === query)
			? {
					value: castedQuery,
					data: {
						reference: castedQuery,
						departmentName: undefined,
						departmentId: undefined,
						id: undefined,
						isArchived: false,
					},
					label: `${t('cases.actions.createNew')} "${castedQuery}"`,
			  }
			: undefined
	}, [isValidQuery, data, user, departmentId, query, t])

	const options = useMemo(
		() => (newCaseOption ? [newCaseOption, ...existingCaseOptions] : existingCaseOptions),
		[newCaseOption, existingCaseOptions]
	)

	const selectedCase = useMemo(() => {
		// existing value
		const foundExisting = options.find(candidate => candidate.value === selectedCaseId && candidate.data.id)
		if (foundExisting) return foundExisting.data
		// new value
		return (
			options.find(candidate => candidate.value === selectedCaseId)?.data ?? {
				reference: selectedCaseId,
				isArchived: false,
			}
		)
	}, [options, selectedCaseId])

	const onPopupScroll = useCallback(
		(e: UIEvent<HTMLDivElement>) => {
			const target = e.target as HTMLDivElement
			if (hasNextPage && target.offsetHeight + target.scrollTop + 200 >= target.scrollHeight) {
				fetchNextPage()
			}
		},
		[hasNextPage, fetchNextPage]
	)

	return {
		query,
		setQuery,
		selectedCaseId,
		setSelectedCaseId,
		selectedCase,
		options,
		isLoading,
		isStillLoading,
		onPopupScroll,
	}
}

export function useBookingInfo(orderId?: ISimplifiedOrder['id']) {
	return useQuery({
		queryKey: [QUERIES.bookingByOrderId, orderId],
		queryFn: async () => (orderId ? await OrdersApi.getBookingInfo(orderId) : undefined),
		enabled: !!orderId,
	})
}

export function useOrderById(orderId?: ISimplifiedOrder['id']) {
	return useQuery({
		queryKey: [QUERIES.orderById, orderId],
		queryFn: async () => (orderId ? await OrdersApi.getDatahubOrderById(orderId) : undefined),
		enabled: !!orderId,
	})
}

export function useOrderByCaseId({
	caseId,
	orderId,
}: {
	caseId?: IDatahubCase['id']
	orderId?: ISimplifiedOrder['id']
}) {
	const query = useSimplifiedCaseOrders(caseId)
	const foundOrder = useMemo(() => query.data?.find(({ id }) => id === orderId), [query.data, orderId])

	return {
		...query,
		data: foundOrder,
	}
}

export function usePropertyTypes() {
	const { locale } = useLocalization()

	const query = useQuery({ queryKey: [QUERIES.propertyTypes], queryFn: () => OrdersApi.getPropertyTypes() })

	const formattedData = useMemo(
		() => query.data?.map(type => mapPropertyTypeFromDto(type, locale)) || [],
		[query.data, locale]
	)

	const propertyTypeOptions = useMemo(
		() =>
			formattedData
				.map(({ key, name }) => ({
					value: key,
					label: name,
				}))
				.sort((prev, next) => (prev.label < next.label ? -1 : 1)),
		[formattedData]
	)

	return {
		...query,
		data: formattedData,
		propertyTypeOptions,
	}
}
